import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { capitalizeFirstLetter, formatOrderDateToLocalTime } from '../../../util';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../popups/ConfirmPopup/helper';
import { showErrorPopup } from '../../popups/ErrorPopup/ErrorPopup';
import * as Styled from '../common/CommonStyledComponents';
import Loading from '../../LoadingComponent/LoadingComponent';
import { Button, FullColoredButton } from '../../common/buttonsComponents';
import { setSelectedTask } from '../../../actions/tools/OrdersSearchActions';
import { addNoteToSelectedTicket } from '../../../actions';
import { extractSelectedInboxTab, extractSelectedTicketFromState, isSpecialist } from '../../../extractFromState';
import { getSelectedOrder } from '../../../selectors/tools';
import {
  amazonCancelRefundAsurionOrderAutomatic,
  amazonGetCancelRefundAsurionOrderInformation,
} from '../../../actions/tools/AmazonActions';
import { handleError } from 'services/ErrorService/ErrorService';

const STATUS_TO_PLACEHOLDER_MAP = {
  UNSHIPPED_CANCEL:
    'This order has not yet been fulfilled so you can cancel it and the customer will receive a refund of their full payment.',
  SHIPPED_FULL_REFUND:
    'The plan has been active less than 12 months, so you can issue a full refund for it. See refund details below. Note: tax cannot be refunded.',
  SHIPPED_PARTIAL_REFUND:
    'The plan has been active more than 12 months, so you can issue partial refund for it. See refund details below. Note: tax cannot be refunded.',
  PARTIALLY_SHIPPED:
    'This order was partially fulfilled so currently you cannot change it. Wait for it to be fully fulfilled.',
  SHIPPED_NO_REFUND:
    "The plan has been active more than 12 months and the order price is less than 12$, so you can't issue refund for it.",
  ALREADY_PROCESSED:
    'Processing for this order has already been scheduled. Ask if the customer waited 1hr or more before asking. Details of request processing below.',
  CASE_OPENED: 'This order has a case open under a plan, not able to cancel/refund at the moment.',
};
const ALREADY_PROCESSED = 'ALREADY_PROCESSED';
const SUBMISSION_ERROR = 'SUBMISSION_ERROR';

class AmazonAsurionCancelRefundOrder extends Component {
  _isMounted = false;

  state = {
    isCancelRefundInfoRequesting: true,
    isCancelRefundActionRequesting: false,
    decision: '',
    processedState: null,
    processedType: null,
    purchaseDate: null,
    orderItemsDetails: [],
    showError: false,
    orderItems: [],
    order: null,
  };

  async componentDidMount() {
    this._isMounted = true;

    try {
      const { decision, processedState, processedType, purchaseDate, orderItemsDetails, orderItems, order } =
        await this.props.amazonGetCancelRefundAsurionOrderInformation();

      if (this._isMounted) {
        this.setState({
          isCancelRefundInfoRequesting: false,
          decision,
          processedState,
          processedType,
          purchaseDate,
          orderItemsDetails,
          orderItems,
          order,
        });
      }

      // display warning message if order cannot be canceled due to status
      if (this.isOrderStatusPending) {
        showErrorPopup('This order has a status of "pending." We cannot cancel pending orders.', 'Oops!');
      }
    } catch (error) {
      handleError({ error });
      this.setState({
        isCancelRefundInfoRequesting: false,
        showError: true,
      });
      showErrorPopup(
        'There was an issue with Amazon when processing this request. Please try again in a few minutes.',
        'Oops!'
      );
      this.props.setSelectedTask();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  get isOrderStatusPending() {
    return this.state.order.OrderStatus === 'Pending';
  }

  get orderId() {
    return this.state.order['AmazonOrderId'];
  }

  get orderTotalObject() {
    return this.state.order['OrderTotal'];
  }

  get orderTotalWithCurrencyCode() {
    // status PENDING orders do not have an OrderTotal attribute
    if (this.orderTotalObject) {
      return `${this.orderTotalObject['Amount']} ${this.orderTotalObject['CurrencyCode']}`;
    } else if (this.isOrderStatusPending) {
      return 'Pending';
    }

    return 'Unavailable';
  }

  get totalForRefund() {
    return this.state.orderItemsDetails.reduce((acc, orderItemDetail) => {
      return acc + orderItemDetail.refundAmount;
    }, 0);
  }

  get actionVerb() {
    if (this.isCancellableOrder) {
      return 'cancel';
    }

    if (this.isRefundableOrder) {
      return 'refund';
    }

    return '';
  }

  get actionName() {
    if (this.isCancellableOrder) {
      return 'cancellation';
    }

    if (this.isRefundableOrder) {
      return 'refund';
    }

    return '';
  }

  get submittedActionName() {
    if (this.state.processedType === 'CANCELLATION') {
      return 'cancelled';
    }

    return 'refunded';
  }

  get placeholderText() {
    const submitionErrorOccured = this.state.processedState === SUBMISSION_ERROR;
    const alreadyProcessed = this.state.decision === ALREADY_PROCESSED;

    if (alreadyProcessed && !submitionErrorOccured) {
      return `Order has been ${this.submittedActionName} successfully. Please go to 'after purchase -- return/refund' to find appropriate confirmation macro`;
    }
    if (submitionErrorOccured) {
      return 'Order hasn’t been canceled. Please try again';
    }

    return STATUS_TO_PLACEHOLDER_MAP[this.state.decision] || '';
  }

  get isCancelRefundButtonAccessible() {
    return ['UNSHIPPED_CANCEL', 'SHIPPED_FULL_REFUND', 'SHIPPED_PARTIAL_REFUND'].includes(this.state.decision);
  }

  get isRefundableOrder() {
    return ['SHIPPED_FULL_REFUND', 'SHIPPED_PARTIAL_REFUND'].includes(this.state.decision);
  }

  get isCancellableOrder() {
    return ['UNSHIPPED_CANCEL'].includes(this.state.decision);
  }

  handleBackBtnClick = () => {
    this.props.setSelectedTask(null);
  };

  handleRefundBtnClick = () => {
    twoBtnPopupCreator({
      content: {
        main: `Are you sure you want to ${this.actionVerb} the order?`,
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: `CONFIRM ${this.actionName.toUpperCase()}`,
      },
      onConfirm: this.confirmationHandler,
    });
  };

  confirmationHandler = async () => {
    this.setState({ isCancelRefundActionRequesting: true });

    try {
      await this.props.amazonCancelRefundAsurionOrderAutomatic();
    } catch (error) {
      handleError({ error });

      return this.handleFailedRefund();
    }

    await this.handleSuccessRefund();
  };

  handleSuccessRefund = async () => {
    this.setState({ isCancelRefundActionRequesting: false });

    this.props.setSelectedTask(null);

    oneBtnPopupCreator({
      content: {
        main: `${capitalizeFirstLetter(
          this.actionName
        )} was scheduled successfully. Please go to 'after purchase -- return/refund' to find appropriate confirmation macro.`,
        iconPath: '/images/checkmark-green.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  handleFailedRefund = () => {
    this.setState({ isCancelRefundActionRequesting: false });

    oneBtnPopupCreator({
      content: {
        main: `There was an error with when scheduling ${this.actionName}`,
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  renderCommonOrderDetails() {
    return [
      <Styled.RowContent key="amazon-order-details-date">
        <div>Purchase date:</div>
        <div>{formatOrderDateToLocalTime(this.state.purchaseDate, 'amazon')}</div>
      </Styled.RowContent>,
      <Styled.RowContent key="amazon-order-details-total">
        <div>Order total:</div>
        <div>{this.orderTotalWithCurrencyCode}</div>
      </Styled.RowContent>,
    ];
  }

  render() {
    if (this.state.showError) {
      return null;
    }

    if (this.state.isCancelRefundInfoRequesting) {
      return <Loading label="Loading order information..." />;
    }

    if (this.state.isCancelRefundActionRequesting) {
      return <Loading label="Processing order..." />;
    }

    return (
      <Styled.OrderTaskWrapper>
        <Styled.OrderTaskTitle>Cancel or Refund Asurion Order</Styled.OrderTaskTitle>
        <Styled.OrderTaskDescription>{this.placeholderText}</Styled.OrderTaskDescription>

        {this.state.decision === ALREADY_PROCESSED && (
          <Styled.CancelStep>
            <Styled.RowContent>
              <div>Request state:</div>
              <div>{this.state.processedState}</div>
            </Styled.RowContent>
            <Styled.Divider />
          </Styled.CancelStep>
        )}

        {this.isCancellableOrder && (
          <Styled.CancelStep>
            {this.renderCommonOrderDetails()}
            <Styled.Divider />
          </Styled.CancelStep>
        )}

        {this.isRefundableOrder && (
          <Styled.CancelStep>
            {this.renderCommonOrderDetails()}
            <Styled.Divider />

            <Styled.RowContent>Items that will be refunded:</Styled.RowContent>
            {this.state.orderItemsDetails.map((orderItemDetail) => {
              const orderItem = this.state.orderItems.find(
                (_orderItem) => _orderItem['OrderItemId'] === orderItemDetail.itemId
              );
              const itemPrice = orderItem['ItemPrice'];

              return [
                <Styled.RowContent key={`amazon-refund-items-${orderItemDetail.itemId}-info`}>
                  <div>
                    {orderItem['QuantityOrdered']} {orderItem['Title']}
                  </div>
                  <div>
                    {orderItemDetail.refundAmount} {itemPrice['CurrencyCode']}
                  </div>
                </Styled.RowContent>,
                <Styled.RowContent key={`amazon-refund-items-${orderItemDetail.itemId}-qtys`}>
                  <div>
                    Warranty end date: {formatOrderDateToLocalTime(orderItemDetail.warrantyEndDate, 'amazon')} (
                    {orderItemDetail.remainingMonths} months remaining)
                  </div>
                  <div>
                    {orderItemDetail.refundAmount} {itemPrice['CurrencyCode']}
                  </div>
                </Styled.RowContent>,
              ];
            })}
            <Styled.Divider />

            <Styled.RowContent>
              <div>Total to refund:</div>
              <div>
                {this.totalForRefund} {this.orderTotalObject['CurrencyCode']}
              </div>
            </Styled.RowContent>
            <Styled.Divider />
          </Styled.CancelStep>
        )}

        <Styled.OrderTaskControls>
          <Button onClick={this.handleBackBtnClick}>BACK</Button>
          {this.isCancelRefundButtonAccessible && (
            <FullColoredButton
              active={!this.isOrderStatusPending}
              disabled={this.isOrderStatusPending}
              onClick={this.handleRefundBtnClick}
            >
              PROCESS ORDER {this.actionName.toUpperCase()}
            </FullColoredButton>
          )}
        </Styled.OrderTaskControls>
      </Styled.OrderTaskWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrder(state),
  selectedTicket: extractSelectedTicketFromState(state),
  selectedInboxTab: extractSelectedInboxTab(state),
  isSpecialist: isSpecialist(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedTask,
      amazonGetCancelRefundAsurionOrderInformation,
      amazonCancelRefundAsurionOrderAutomatic,
      addNoteToSelectedTicket,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AmazonAsurionCancelRefundOrder);
